import { TuiDrawer } from '@taiga-ui/kit';
import { TuiAppBar } from '@taiga-ui/layout';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { ThemeSelectorComponent } from '@core/components/theme-selector/theme-selector.component';
import { RequestFormChangeStatusButtonComponent } from '@features/requests/request-form/components/request-form-change-status-button/request-form-change-status-button.component';
import { ProfileComponent } from '@core/components/profile/profile.component';
import {
  TuiGroup,
  TuiIcon,
  TuiLink,
  TuiButton,
  TuiPopup,
} from '@taiga-ui/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { AppVersionComponent } from '@core/components/app-version/app-version.component';
import { NavigationService } from '@core/services';
import { AccountService } from '@core/services/account/account.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestFiltersService } from '@core/services/request/request-filters.service';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import {
  MOBILE_REQUESTS_LIST_COMPACT_MODE,
  PREFERRED_LANG_LOCAL_STORAGE,
} from '@core/constants';
import { Languages } from '@core/types';
import { environment } from '@environment/environment';
import { filter, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SvgIconComponent } from '@common/components/svg-icon/svg-icon.component';

@Component({
  selector: 'x-sidebar',
  imports: [
    SvgIconComponent,
    TuiDrawer,
    TuiPopup,
    ThemeSelectorComponent,
    ProfileComponent,
    TuiIcon,
    TuiLink,
    RouterLink,
    TuiAppBar,
    TuiButton,
    NgClass,
    AppVersionComponent,
    AsyncPipe,
    RequestFormChangeStatusButtonComponent,
    TranslatePipe,
    TuiGroup,
    NgTemplateOutlet,
  ],
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  private readonly router = inject(Router);
  protected readonly permissionService = inject(OutkeepPermissionService);
  protected readonly accountService = inject(AccountService);
  protected readonly navigationService = inject(NavigationService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly requestFiltersService = inject(RequestFiltersService);
  private readonly destroyRef = inject(DestroyRef);

  public isSidebarOpen = false;

  public $mobileRequestsListCompactMode =
    this.requestFiltersService.$mobileRequestsListCompactMode;

  public languageChangeEnabled = environment.xOutkeepEnableLanguageChange;

  $isMobile = input.required<boolean>({ alias: 'isMobile' });
  $navbarHeader = input.required<string>({ alias: 'navbarHeader' });
  $canGoBack = input<boolean>(false, { alias: 'canGoBack' });
  $isRequestPage = input<boolean>(false, { alias: 'isRequestPage' });
  $closeSidebarInput = input<void>(undefined, { alias: 'closeSidebar' });

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.isSidebarOpen) {
          this.closeSidebar();
        }
      });
  }

  public openSidebar(): void {
    this.isSidebarOpen = true;
  }

  public closeSidebar(): void {
    this.isSidebarOpen = false;
  }

  onAuxClick(event: MouseEvent, link: string): void {
    if (event.button === 1 || event.ctrlKey) {
      window.open(link, '_blank');
    }
  }

  setEnglishLang() {
    this.outkeepTranslateService.setEnglishLang();
    this.localStorageService.setItem(
      PREFERRED_LANG_LOCAL_STORAGE,
      Languages.ENGLISH,
    );
  }

  setRussianLang() {
    this.outkeepTranslateService.setRussianLang();
    this.localStorageService.setItem(
      PREFERRED_LANG_LOCAL_STORAGE,
      Languages.RUSSIAN,
    );
  }

  setTurkishLang() {
    this.outkeepTranslateService.setTurkishLang();
    this.localStorageService.setItem(
      PREFERRED_LANG_LOCAL_STORAGE,
      Languages.TURKISH,
    );
  }

  toggleMobileRequestsListCompactMode() {
    const value = this.requestFiltersService.$mobileRequestsListCompactMode();

    if (!value) {
      this.localStorageService.setItem(MOBILE_REQUESTS_LIST_COMPACT_MODE, true);
      this.requestFiltersService.$mobileRequestsListCompactMode.set(true);
      this.accountService
        .createOrUpdateUserSettings$({
          outkeep: { mobileRequestsListCompactMode: true },
        })
        .pipe(take(1))
        .subscribe();
    } else {
      this.localStorageService.removeItem(MOBILE_REQUESTS_LIST_COMPACT_MODE);
      this.requestFiltersService.$mobileRequestsListCompactMode.set(false);
      this.accountService
        .createOrUpdateUserSettings$({
          outkeep: { mobileRequestsListCompactMode: false },
        })
        .pipe(take(1))
        .subscribe();
    }
  }
}
